import { StepConfig } from 'components/new-ui/composed/multistep-form/types';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import JobStep from './steps/JobStep';
import CustomerStep from './steps/CustomerStep';
import FormTypeStep from './steps/FormTypeStep';
import EnsFormStep from './steps/EnsFormStep';
import DeclarationStep from './steps/DeclarationStep';
import TemplateStep from './steps/TemplateStep';
import InvoiceStep from './steps/InvoiceStep';
import BulkUploadStep from './steps/BulkUploadStep';
import { BrokerUploadFormFields, BrokerUploadSteps } from './types';
import {
    jobValidationSchema,
    customerValidationSchema,
    formTypeValidationSchema,
    declarationValidationSchema,
    templateValidationSchema,
    invoiceValidationSchema,
    bulkUploadValidationSchema,
    manifestValidationSchema,
    ensFormValidationSchema,
} from './validationSchema';
import ManifestStep from './steps/ManifestStep';

type StepsOptions = {
    jobLocked: boolean;
    manifestLocked: boolean;
    formTypePreSelected: boolean;
    isDuplicating: boolean;
};

export const getBrokerUploadStepsConfig = (
    country: DeclarationCountry | undefined,
    type: DeclarationInternalType | undefined,
    { jobLocked, manifestLocked, formTypePreSelected, isDuplicating }: StepsOptions
): StepConfig<BrokerUploadFormFields>[] => {
    const isIreland = country === DeclarationCountry.IRELAND;
    const isUK = country === DeclarationCountry.UK;
    const isEU = country === DeclarationCountry.EU;

    const isICS2ENS = isEU && type === DeclarationInternalType.ENS;
    const isICS2ArrivalNotification = isEU && type === DeclarationInternalType.ARRIVAL_NOTIFICATION_ICS2;
    const isIrelandPBN = isIreland && type === DeclarationInternalType.PBN;
    const isIrelandETD = isIreland && type === DeclarationInternalType.ETD;
    const isArrival = isIreland && type === DeclarationInternalType.ARRIVAL;
    const isIrelandImport = isIreland && type === DeclarationInternalType.IMPORT;
    const isIrelandExport = isIreland && type === DeclarationInternalType.EXPORT;
    const isIrelandNCTS = isIreland && type === DeclarationInternalType.NCTS;
    const isUKImport = isUK && type === DeclarationInternalType.IMPORT;
    const isUKExport = isUK && type === DeclarationInternalType.EXPORT;
    const isUKNCTS = isUK && type === DeclarationInternalType.NCTS;

    const showManifest = isICS2ENS || isICS2ArrivalNotification;
    const showJob = !isICS2ENS && !isICS2ArrivalNotification;
    // The arrival notification has a single message type (IE3N06), so there is no form type to pick.
    const showFormType = !isIrelandPBN && !isIrelandETD && !isArrival && !isICS2ArrivalNotification;
    const showTemplate =
        isIrelandImport ||
        isIrelandExport ||
        isIrelandPBN ||
        isIrelandNCTS ||
        isUKImport ||
        isUKExport ||
        isUKNCTS ||
        isICS2ENS;
    const showInvoice = isIrelandImport || isIrelandNCTS;
    const showBulkUpload = false;
    const showDeclaration = isArrival;

    const steps: StepConfig<BrokerUploadFormFields>[] = [];

    if (showManifest) {
        steps.push({
            key: BrokerUploadSteps.MANIFEST,
            label: 'Manifest',
            component: ManifestStep,
            validationSchema: manifestValidationSchema,
            disabled: manifestLocked,
        });
    }

    if (showJob) {
        steps.push({
            key: BrokerUploadSteps.JOB,
            label: 'Job',
            component: JobStep,
            validationSchema: jobValidationSchema,
            disabled: jobLocked,
        });

        steps.push({
            key: BrokerUploadSteps.CUSTOMER,
            label: 'Customer',
            component: CustomerStep,
            validationSchema: customerValidationSchema,
            disabled: jobLocked,
        });
    }

    if (showDeclaration) {
        steps.push({
            key: BrokerUploadSteps.DECLARATION,
            label: 'Declaration',
            component: DeclarationStep,
            validationSchema: declarationValidationSchema,
        });
    }

    if (showFormType) {
        steps.push(
            isICS2ENS
                ? {
                      key: BrokerUploadSteps.FORM_TYPE,
                      label: 'Form',
                      component: EnsFormStep,
                      validationSchema: ensFormValidationSchema,
                      disabled: formTypePreSelected,
                  }
                : {
                      key: BrokerUploadSteps.FORM_TYPE,
                      label: 'Form',
                      component: FormTypeStep,
                      validationSchema: formTypeValidationSchema,
                      disabled: formTypePreSelected,
                  }
        );
    }

    if (showTemplate) {
        steps.push({
            key: BrokerUploadSteps.TEMPLATE,
            label: 'Template (Optional)',
            component: TemplateStep,
            validationSchema: templateValidationSchema,
            optional: true,
            disabled: isDuplicating,
        });
    }

    if (showInvoice) {
        steps.push({
            key: BrokerUploadSteps.INVOICE,
            label: 'Invoice (Optional)',
            component: InvoiceStep,
            validationSchema: invoiceValidationSchema,
            optional: true,
        });
    }

    if (showBulkUpload) {
        steps.push({
            key: BrokerUploadSteps.BULK_UPLOAD,
            label: 'Bulk Upload (Optional)',
            component: BulkUploadStep,
            validationSchema: bulkUploadValidationSchema,
            optional: true,
        });
    }

    return steps;
};
